/**
 * EBlast deployment detail + the eblast report bridge.
 *
 * EblastDeployScreen — the metrics for one email drop (`email_campaigns._id`), with a Report button that
 * opens the eblast report builder for the drop's SO.
 * EblastReportScreen — generates (or re-opens) the email report for a `report_so` and forwards to the
 * builder once the worker has written it. Mirrors the RTB CampaignReportScreen's queue-and-poll UX.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, email, reports, builder, type EmailDeployDetail } from '../lib/api';
import { stripApiPrefix } from '../lib/eblast-title';

const int = (n: number) => (n || 0).toLocaleString('en-US');
const pct = (n: number) => `${(Number(n) || 0).toFixed(2)}%`;
const dt = (v: string | null) => (v ? v.slice(0, 16).replace('T', ' ') : '—');

function Metric({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="metric">
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
      {sub && <div className="metric-sub">{sub}</div>}
    </div>
  );
}

export function EblastDeployScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState<EmailDeployDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    email.deploy(id)
      .then((r) => { if (live) setD(r); })
      .catch((e) => { if (live) setError(e instanceof ApiError ? e.message : String(e)); });
    return () => { live = false; };
  }, [id]);

  if (error) return <div className="banner error">{error}</div>;
  if (!d) return <div className="empty">Loading deployment…</div>;

  const clicksTotal = d.mobileClicks + d.nonMobileClicks;
  return (
    <div className="deploy-detail">
      <div className="crumbs">
        <button className="lnk" onClick={() => navigate('/eblast')}>← EBlast Campaigns</button>
      </div>

      <header className="deploy-head">
        <div>
          <h1>{stripApiPrefix(d.name) || '(untitled deployment)'}</h1>
          <div className="deploy-meta">
            <code className="type">{d.so || '—'}</code>
            <span className={`pill ${/active|complet/i.test(d.status) ? 'ok' : 'off'}`}>{d.status || '—'}</span>
            {d.client && <span className="muted">{d.client}</span>}
          </div>
          <button className="button primary"
            onClick={() => navigate(`/eblast/report/${encodeURIComponent(d.reportSo || d.so)}`)}>
            Report
          </button>
        </div>
        <dl className="deploy-facts">
          <dt>IO</dt><dd><code className="type">{d.io || '—'}</code></dd>
          <dt>Report SO</dt><dd><code className="type">{d.reportSo || '—'}</code></dd>
          <dt>Geo type</dt><dd>{d.geotype || '—'}</dd>
          <dt>Deploy start</dt><dd className="num">{dt(d.startDatetime)}</dd>
          <dt>Deploy end</dt><dd className="num">{dt(d.endDatetime)}</dd>
          <dt>Created</dt><dd className="num">{dt(d.createDatetime)}</dd>
        </dl>
      </header>

      <div className="metric-grid">
        <Metric label="Records" value={int(d.totalRecords)} />
        <Metric label="Interactions" value={int(d.totalOpens)} sub={pct(d.opensPct)} />
        <Metric label="Clicks" value={int(d.totalClicks)} sub={pct(d.clicksPct)} />
        {clicksTotal > 0 && <Metric label="Mobile clicks" value={int(d.mobileClicks)}
          sub={`${pct((d.mobileClicks / clicksTotal) * 100)} of clicks`} />}
        {clicksTotal > 0 && <Metric label="Desktop clicks" value={int(d.nonMobileClicks)}
          sub={`${pct((d.nonMobileClicks / clicksTotal) * 100)} of clicks`} />}
      </div>

      <div className="deploy-cols">
        <section className="deploy-creative">
          <h2 className="section-title">Creative</h2>
          <Creative url={d.creativeUrl} />
        </section>

        <section className="deploy-links">
          <h2 className="section-title">
            Clicks by link
            {d.links.length > 0 && <span className="muted"> · {int(d.linkClicksTotal)} attributed</span>}
          </h2>
          {d.links.length === 0 ? (
            <p className="muted">No link clicks recorded for this deployment.</p>
          ) : (
            <div className="table-scroll">
              <table className="links-table">
                <thead>
                  <tr>
                    <th>Destination</th>
                    <th style={{ textAlign: 'right' }}>Clicks</th>
                    <th style={{ textAlign: 'right' }}>Unique</th>
                    <th style={{ textAlign: 'right' }}>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {d.links.map((l) => (
                    <tr key={l.link}>
                      <td className="link-cell">
                        <a href={l.link} target="_blank" rel="noreferrer noopener">{l.link}</a>
                      </td>
                      <td style={{ textAlign: 'right' }} className="num">{int(l.clicks)}</td>
                      <td style={{ textAlign: 'right' }} className="num">{int(l.uniqueClicks)}</td>
                      <td style={{ textAlign: 'right' }} className="num muted">
                        {d.linkClicksTotal > 0 ? pct((l.clicks / d.linkClicksTotal) * 100) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/** The deployment creative screenshot, with a graceful fallback when it is missing or fails to load. */
function Creative({ url }: { url: string | null }) {
  const [failed, setFailed] = useState(false);
  if (!url || failed) return <div className="creative-empty">No creative screenshot available.</div>;
  return (
    <a href={url} target="_blank" rel="noreferrer noopener" className="creative-frame">
      <img src={url} alt="Email creative" loading="lazy" onError={() => setFailed(true)} />
    </a>
  );
}

export function EblastReportScreen() {
  const { so = '' } = useParams();
  const navigate = useNavigate();
  const [asking, setAsking] = useState(false);
  const [waited, setWaited] = useState(0);
  const [reportId, setReportId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Once a job is queued, poll the builder until the worker has written the document, then forward.
  useEffect(() => {
    if (!reportId) return;
    let live = true;
    const timer = setInterval(async () => {
      if (!live) return;
      setWaited((w) => w + 1);
      try {
        await builder.load(reportId);
        if (live) navigate(`/reports/${encodeURIComponent(reportId)}`, { replace: true });
      } catch { /* not written yet — keep polling */ }
    }, 2000);
    return () => { live = false; clearInterval(timer); };
  }, [reportId, navigate]);

  async function generate() {
    if (!so) return;
    setAsking(true);
    setError(null);
    try {
      const r = await reports.generate({ reportSo: so });
      setReportId(r.reportId);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setAsking(false);
    }
  }

  return (
    <div className="report-bridge">
      <div className="crumbs">
        <button className="lnk" onClick={() => navigate('/eblast')}>← EBlast Campaigns</button>
      </div>
      <h1>EBlast report — <code className="type">{so}</code></h1>
      {error && <div className="banner error">{error}</div>}
      {!reportId ? (
        <>
          <p className="muted">Generate (or re-open) the email report for this SO and edit it in the report builder.</p>
          <button className="button primary" onClick={generate} disabled={asking}>
            {asking ? 'Requesting…' : 'Open eblast report'}
          </button>
        </>
      ) : (
        <p className="muted">
          Building your eblast report… this can take a moment while the worker renders it.
          {waited > 0 && ` (${waited * 2}s)`}
          {waited > 60 && <><br />Still working — if this does not resolve, a report worker may not be running.</>}
        </p>
      )}
    </div>
  );
}
